import { useEffect, useState } from 'react';
import FacebookBrowserModal from './FacebookBrowserModal';
import FacebookBrowserModalV1 from './FacebookBrowserModalV1';
import { useConfig } from '../context/ConfigContext';
import { isFacebookInAppBrowser } from '../utils/inAppBrowserUtil';
import { isUnderRoute } from '../utils/routeUtil';
import { resolveBrowserPromptOption } from '../utils/browserPromptUtil';
import {
  BROWSER_PROMPT_CONFIG_KEY,
  BROWSER_PROMPT_VERSIONS,
  FACEBOOK_BROWSER_MODAL_DELAY_MS,
  UI_PREVIEW_ROUTE_PATH,
} from '../constants/bookingConst';

/**
 * Shows the "Open in your browser" popup to guests inside Facebook's or
 * Messenger's in-app browser, in the version picked under Config -> Theme ->
 * Open in Browser Popup (Version 1 by default). Renders nothing anywhere else.
 *
 * It appears FACEBOOK_BROWSER_MODAL_DELAY_MS after the page loads, so the
 * guest sees the site first. The version is read from `landingConfig`
 * (cache / build snapshot, then live) rather than the live `config`, because
 * the popup can open before sign-in finishes — the same source the guest
 * landing page renders from. Kept off the UI preview page, which shows the
 * popups itself.
 *
 * @returns {JSX.Element|null}
 */
function FacebookBrowserPrompt() {
  const { config, landingConfig } = useConfig();
  // The UA and route are read once: neither changes mid-visit in a way that matters here.
  const [shouldPrompt] = useState(() => (
    isFacebookInAppBrowser() && !isUnderRoute(window.location.pathname, [UI_PREVIEW_ROUTE_PATH])
  ));
  const [isOpen, setIsOpen] = useState(false);

  // Opens after the delay. The cleanup cancels a pending timer if the
  // component unmounts first, so it can never fire on a page that's gone.
  useEffect(() => {
    if (!shouldPrompt) return undefined;
    const timer = window.setTimeout(() => setIsOpen(true), FACEBOOK_BROWSER_MODAL_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [shouldPrompt]);

  if (!isOpen) return null;

  const version = resolveBrowserPromptOption((landingConfig || config)?.[BROWSER_PROMPT_CONFIG_KEY]).value;

  // Version 2 is not closable by design; Version 1 dismisses for this visit.
  return version === BROWSER_PROMPT_VERSIONS.V2
    ? <FacebookBrowserModal />
    : <FacebookBrowserModalV1 onClose={() => setIsOpen(false)} />;
}

export default FacebookBrowserPrompt;
